import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Upload } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import SirenSearch from "./SirenSearch";
import AddressSearch from "./AddressSearch";

const ClientForm = ({ client, onSave, onCancel, isEditing = false }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [showOtherElecSupplier, setShowOtherElecSupplier] = useState(false);
  const [showOtherGazSupplier, setShowOtherGazSupplier] = useState(false);
  const [formData, setFormData] = useState(client || {
    nom: "",
    prenom: "",
    company_name: "",
    siren: "",
    etat_dossier: "nouveau",
    adresse: "",
    code_postal: "",
    ville: "",
    telephone_portable: "",
    telephone_fixe: "",
    email: "",
    date_rdv: null,
    date_r2: null,
    date_signature: null,
    fournisseur_elec_actuel: "",
    echeance_elec: null,
    puissance_souscrite: "",
    pdl_elec: "",
    fournisseur_gaz_actuel: "",
    echeance_gaz: null,
    profil_gaz: "",
    pdl_gaz: "",
    note_solvabilite: "",
    commentaires: "",
    telepro: false,
    apporteur: "",
    nouveau_fournisseur: "",
    debut_contrat: null,
    fin_contrat: null,
    commission: "",
    formule_paiement: "mensuel",
    status: "prospect"
  });

  const [documents, setDocuments] = useState({
    facture_elec: null,
    facture_gaz: null,
    acd_signe: null,
    contrat: null
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field, date) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleFileChange = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const token = localStorage.getItem('token');
      console.log('Fetching suppliers from:', `${process.env.REACT_APP_BACKEND_URL}/api/suppliers`);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Suppliers fetched:', response.data);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  useEffect(() => {
    console.log('ClientForm mounted, fetching suppliers...');
    fetchSuppliers();
    
    // Détecter si "Autre" au chargement (mode édition)
    if (client) {
      const commonSuppliers = ['EDF', 'Engie', 'Total Énergies', 'Vattenfall', 'Eni'];
      if (client.fournisseur_elec_actuel && !commonSuppliers.includes(client.fournisseur_elec_actuel)) {
        setShowOtherElecSupplier(true);
      }
      if (client.fournisseur_gaz_actuel && !commonSuppliers.includes(client.fournisseur_gaz_actuel)) {
        setShowOtherGazSupplier(true);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Format data for API
    const clientData = {
      ...formData,
      puissance_souscrite: parseFloat(formData.puissance_souscrite) || 0,
      commission: parseFloat(formData.commission) || null,
      // Convert dates to ISO strings (handle both Date objects and strings)
      date_rdv: formData.date_rdv ? (formData.date_rdv instanceof Date ? formData.date_rdv.toISOString() : formData.date_rdv) : null,
      date_r2: formData.date_r2 ? (formData.date_r2 instanceof Date ? formData.date_r2.toISOString() : formData.date_r2) : null,
      date_signature: formData.date_signature ? (formData.date_signature instanceof Date ? formData.date_signature.toISOString() : formData.date_signature) : null,
      echeance_elec: formData.echeance_elec ? (formData.echeance_elec instanceof Date ? formData.echeance_elec.toISOString() : formData.echeance_elec) : null,
      echeance_gaz: formData.echeance_gaz ? (formData.echeance_gaz instanceof Date ? formData.echeance_gaz.toISOString() : formData.echeance_gaz) : null,
      debut_contrat: formData.debut_contrat ? (formData.debut_contrat instanceof Date ? formData.debut_contrat.toISOString() : formData.debut_contrat) : null,
      fin_contrat: formData.fin_contrat ? (formData.fin_contrat instanceof Date ? formData.fin_contrat.toISOString() : formData.fin_contrat) : null,
    };

    onSave(clientData, documents);
  };

  const DatePickerField = ({ label, value, onChange, required = false }) => (
    <div>
      <Label>{label} {required && "*"}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "dd/MM/yyyy", { locale: fr }) : "Sélectionner une date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
            locale={fr}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  const handleCompanySelect = (companyData) => {
    // Debug pour voir les données reçues
    console.log("Company data received:", companyData);
    
    setFormData(prev => ({
      ...prev,
      // SIREN - seulement le numéro
      siren: companyData.siren,
      // Raison sociale
      company_name: companyData.raison_sociale,
      // Dirigeant - nom simple (sans duplication)
      nom: companyData.nom || prev.nom,
      prenom: companyData.prenom || prev.prenom,
      // Adresse complète - forcer le remplissage avec n'importe quelle adresse disponible
      adresse: companyData.adresse || "Adresse à compléter",
      code_postal: companyData.code_postal || "",
      ville: companyData.ville || "",
      // Informations complémentaires en commentaires
      commentaires: prev.commentaires ? 
        `${prev.commentaires}\nActivité: ${companyData.activite || ''}\nForme juridique: ${companyData.forme_juridique || ''}` :
        `Activité: ${companyData.activite || ''}\nForme juridique: ${companyData.forme_juridique || ''}`
    }));
    
    // Toast informatif avec info sur l'adresse
    if (companyData.nom && companyData.prenom) {
      toast.success(`✅ Données auto-remplies: ${companyData.raison_sociale} - ${companyData.prenom} ${companyData.nom}`);
    } else {
      toast.success(`✅ Entreprise sélectionnée: ${companyData.raison_sociale}`);
    }
    
    // Toast additionnel si adresse trouvée
    if (companyData.adresse && companyData.adresse !== "Adresse non disponible") {
      toast.info(`📍 Adresse remplie: ${companyData.adresse}`);
    }
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      adresse: addressData.adresse,
      code_postal: addressData.code_postal || "",
      ville: addressData.ville || "",
      // Sauvegarder les coordonnées si disponibles
      coordonnees: addressData.coordonnees || null
    }));
  };

  const FileUploadField = ({ label, docType, accept = ".pdf,.jpg,.png" }) => {
    const fileInputRef = React.useRef(null);
    
    const handleIconClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileChange(docType, file);
      }
    };

    return (
      <div>
        <Label>{label}</Label>
        <div className="flex items-center space-x-2">
          <Input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="flex-1"
            style={{ display: 'none' }}
          />
          <Input
            type="text"
            placeholder="Aucun fichier sélectionné"
            value={documents[docType]?.name || ""}
            readOnly
            className="flex-1"
            onClick={handleIconClick}
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={handleIconClick}
            className="shrink-0"
          >
            <Upload className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-h-[80vh] overflow-y-auto">
      {/* Informations personnelles */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Informations Personnelles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="nom">Nom *</Label>
            <Input
              id="nom"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="prenom">Prénom *</Label>
            <Input
              id="prenom"
              value={formData.prenom}
              onChange={(e) => handleInputChange('prenom', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="company_name">Raison Sociale *</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              required
            />
          </div>
          <div>
            <SirenSearch
              value={formData.siren}
              onChange={(value) => handleInputChange('siren', value)}
              onCompanySelect={handleCompanySelect}
            />
          </div>
          <div>
            <Label htmlFor="etat_dossier">État du Dossier</Label>
            <Select value={formData.etat_dossier} onValueChange={(value) => handleInputChange('etat_dossier', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="signe">Signé</SelectItem>
                <SelectItem value="refuse">Refusé</SelectItem>
                <SelectItem value="attente">En attente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Adresse et Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Adresse et Contact</h3>
        
        {/* 3 champs séparés pour l'adresse */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6">
            <Label htmlFor="adresse">Adresse</Label>
            <Input
              id="adresse"
              value={formData.adresse}
              onChange={(e) => handleInputChange('adresse', e.target.value)}
              placeholder="Numéro et rue"
            />
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="code_postal">Code Postal</Label>
            <Input
              id="code_postal"
              value={formData.code_postal}
              onChange={(e) => handleInputChange('code_postal', e.target.value)}
              placeholder="75001"
            />
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="ville">Ville</Label>
            <Input
              id="ville"
              value={formData.ville}
              onChange={(e) => handleInputChange('ville', e.target.value)}
              placeholder="Paris"
            />
          </div>
        </div>

        {/* Recherche d'adresse optionnelle */}
        <div className="border-t pt-4">
          <Label className="text-sm text-gray-600">Recherche d'adresse (optionnel)</Label>
          <AddressSearch
            value=""
            onChange={() => {}} // Ne pas utiliser onChange
            onAddressSelect={handleAddressSelect}
            label="Rechercher une adresse"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="telephone_portable">Téléphone Portable</Label>
            <Input
              id="telephone_portable"
              value={formData.telephone_portable}
              onChange={(e) => handleInputChange('telephone_portable', e.target.value)}
              placeholder="06 12 34 56 78"
            />
          </div>
          <div>
            <Label htmlFor="telephone_fixe">Téléphone Fixe</Label>
            <Input
              id="telephone_fixe"
              value={formData.telephone_fixe}
              onChange={(e) => handleInputChange('telephone_fixe', e.target.value)}
              placeholder="01 42 33 44 55"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      {/* Dates Importantes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Dates Importantes</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatePickerField
            label="Date RDV"
            value={formData.date_rdv}
            onChange={(date) => handleDateChange('date_rdv', date)}
          />
          <DatePickerField
            label="Date R2"
            value={formData.date_r2}
            onChange={(date) => handleDateChange('date_r2', date)}
          />
          <DatePickerField
            label="Date Signature"
            value={formData.date_signature}
            onChange={(date) => handleDateChange('date_signature', date)}
          />
        </div>
      </div>

      {/* Électricité */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Électricité</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fournisseur_elec_actuel">Fournisseur Électricité Actuel</Label>
            <Select 
              value={showOtherElecSupplier ? "Autre" : formData.fournisseur_elec_actuel} 
              onValueChange={(value) => {
                if (value === "Autre") {
                  setShowOtherElecSupplier(true);
                  handleInputChange('fournisseur_elec_actuel', '');
                } else {
                  setShowOtherElecSupplier(false);
                  handleInputChange('fournisseur_elec_actuel', value);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EDF">EDF</SelectItem>
                <SelectItem value="Engie">Engie</SelectItem>
                <SelectItem value="Total Énergies">Total Énergies</SelectItem>
                <SelectItem value="Vattenfall">Vattenfall</SelectItem>
                <SelectItem value="Eni">Eni</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
            {showOtherElecSupplier && (
              <Input
                className="mt-2"
                placeholder="Précisez le fournisseur d'électricité"
                value={formData.fournisseur_elec_actuel}
                onChange={(e) => handleInputChange('fournisseur_elec_actuel', e.target.value)}
              />
            )}
          </div>
          <DatePickerField
            label="Échéance Électricité"
            value={formData.echeance_elec}
            onChange={(date) => handleDateChange('echeance_elec', date)}
          />
          <div>
            <Label htmlFor="puissance_souscrite">Puissance Souscrite (kVA) *</Label>
            <Input
              id="puissance_souscrite"
              type="number"
              step="0.1"
              value={formData.puissance_souscrite}
              onChange={(e) => handleInputChange('puissance_souscrite', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="pdl_elec">PDL Électricité</Label>
            <Input
              id="pdl_elec"
              value={formData.pdl_elec}
              onChange={(e) => handleInputChange('pdl_elec', e.target.value)}
              placeholder="Point de Livraison"
            />
          </div>
        </div>
      </div>

      {/* Gaz */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Gaz</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fournisseur_gaz_actuel">Fournisseur Gaz Actuel</Label>
            <Select 
              value={showOtherGazSupplier ? "Autre" : formData.fournisseur_gaz_actuel} 
              onValueChange={(value) => {
                if (value === "Autre") {
                  setShowOtherGazSupplier(true);
                  handleInputChange('fournisseur_gaz_actuel', '');
                } else {
                  setShowOtherGazSupplier(false);
                  handleInputChange('fournisseur_gaz_actuel', value);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Engie">Engie</SelectItem>
                <SelectItem value="Total Énergies">Total Énergies</SelectItem>
                <SelectItem value="Eni">Eni</SelectItem>
                <SelectItem value="Vattenfall">Vattenfall</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
            {showOtherGazSupplier && (
              <Input
                className="mt-2"
                placeholder="Précisez le fournisseur de gaz"
                value={formData.fournisseur_gaz_actuel}
                onChange={(e) => handleInputChange('fournisseur_gaz_actuel', e.target.value)}
              />
            )}
          </div>
          <DatePickerField
            label="Échéance Gaz"
            value={formData.echeance_gaz}
            onChange={(date) => handleDateChange('echeance_gaz', date)}
          />
          <div>
            <Label htmlFor="profil_gaz">Profil Gaz</Label>
            <Select value={formData.profil_gaz} onValueChange={(value) => handleInputChange('profil_gaz', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="T1">T1 - Base</SelectItem>
                <SelectItem value="T2">T2 - B0</SelectItem>
                <SelectItem value="T3">T3 - B1</SelectItem>
                <SelectItem value="T4">T4 - B2i</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pdl_gaz">PDL Gaz</Label>
            <Input
              id="pdl_gaz"
              value={formData.pdl_gaz}
              onChange={(e) => handleInputChange('pdl_gaz', e.target.value)}
              placeholder="Point de Livraison Gaz"
            />
          </div>
        </div>
      </div>

      {/* Données Commerciales */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold border-b pb-2">Données Commerciales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="note_solvabilite">Note de Solvabilité (0-10)</Label>
            <Input
              id="note_solvabilite"
              type="number"
              min="0"
              max="10"
              step="0.1"
              placeholder="Note entre 0 et 10"
              value={formData.note_solvabilite}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (value >= 0 && value <= 10) {
                  handleInputChange('note_solvabilite', e.target.value);
                } else if (e.target.value === '') {
                  handleInputChange('note_solvabilite', '');
                }
              }}
            />
          </div>
          <div>
            <Label htmlFor="apporteur">Apporteur</Label>
            <Input
              id="apporteur"
              value={formData.apporteur}
              onChange={(e) => handleInputChange('apporteur', e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="telepro"
              checked={formData.telepro}
              onCheckedChange={(checked) => handleInputChange('telepro', checked)}
            />
            <Label htmlFor="telepro">Téléprospection</Label>
          </div>
          <div>
            <Label htmlFor="commission">Commission (€)</Label>
            <Input
              id="commission"
              type="number"
              step="0.01"
              value={formData.commission}
              onChange={(e) => handleInputChange('commission', e.target.value)}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="commentaires">Commentaires</Label>
          <Textarea
            id="commentaires"
            rows={3}
            value={formData.commentaires}
            onChange={(e) => handleInputChange('commentaires', e.target.value)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
          {isEditing ? "Modifier le Client" : "Enregistrer le Client"}
        </Button>
      </div>
    </form>
  );
};

export default ClientForm;